/* eslint-disable @typescript-eslint/no-explicit-any */
import {ContractProduct} from '@api'
import {formatCurrency, getTotalContractCost} from '@common'

import {ContractHTMLData} from './contract-pdf-overview-model'

const templateLiterals = [
  {value: 'company.name', key: 'COMPANY_NAME'},
  {value: 'contractCode', key: 'CONTRACT_CODE'},
  {value: 'createdAt', key: 'CREATION_DATE'},
  {value: 'contractType.name', key: 'CONTRACT_NAME'},
  {value: 'customer.firstName', key: 'CUSTOMER_FIRST_NAME'},
  {value: 'customer.lastName', key: 'CUSTOMER_LAST_NAME'},
  {value: 'customer.email', key: 'CUSTOMER_EMAIL'},
  {value: 'customer.phone', key: 'CUSTOMER_PHONE'},
  {value: 'customer.street', key: 'CUSTOMER_STREET'},
  {value: 'customer.personalNumber', key: 'CUSTOMER_PERSONAL_NO'},
  {value: 'customer.zipCode', key: 'CUSTOMER_ZIP_CODE'},
  {value: 'customer.city', key: 'CUSTOMER_CITY'},
  {value: 'dealer.name', key: 'DEALER_NAME'},
  {value: 'dealer.vatNumber', key: 'DEALER_VAT_NUMBER'},
  {value: 'dealer.companyNo', key: 'DEALER_COMPANY_NO'},
  {value: 'dealer.invoicePhone', key: 'DEALER_PHONE'},
  {value: 'officialSignatory', key: 'OFFICIAL_SIGNATORY'},
  {value: 'dealer.dealerNo', key: 'DEALER_NO'},
  {value: 'dealer.email', key: 'DEALER_EMAIL'},
  {value: 'dealer.adress', key: 'DEALER_ADDRESS'},
  {value: 'dealer.zipCode', key: 'DEALER_ZIP_CODE'},
  {value: 'dealer.city', key: 'DEALER_CITY'},
  {value: 'length', key: 'TOTAL_LENGTH'},
  {value: 'disclaimer', key: 'TERMS_DISCLAIMER'},
]

function getNestedValue<T extends object>(obj: T, path: string) {
  return path.split('.').reduce((acc, part) => acc && (acc as any)[part], obj)
}

function replaceTemplateKeys(template: string, data: any, mappings: {value: string; key: string}[]) {
  let result = template

  for (const {key, value} of mappings) {
    const actualValue = getNestedValue(data, value) ?? ''

    // Match both ${KEY} and KEY
    const regex = new RegExp(`(\\$\\{${key}\\}|${key})`, 'g')
    result = result.replace(regex, actualValue)
  }

  return result
}

function generateProductRows(
  template: string,
  products: ContractHTMLData['products'] | ContractProduct['accessories'],
  months: number,
  currency: string,
) {
  return products
    .map((product, index) => {
      return template
        .replace(/(\$\{PRODUCT_ORDER_NO\}|PRODUCT_ORDER_NO)/g, String(index + 1))
        .replace(/(\$\{PRODUCT_QTY\}|PRODUCT_QTY)/g, String(product.quantity || 0))
        .replace(/(\$\{PRODUCT_NAME\}|PRODUCT_NAME)/g, product.name || '')
        .replace(/(\$\{PRODUCT_SERIAL_NO\}|PRODUCT_SERIAL_NO)/g, product.serialNumber || '')
        .replace(
          /(\$\{PRODUCT_INSURANCE\}|PRODUCT_INSURANCE)/g,
          formatCurrency(product.insurancePrice || '0', currency),
        )
        .replace(/(\$\{PRODUCT_SERVICE\}|PRODUCT_SERVICE)/g, formatCurrency(product.servicePrice || '0', currency))
        .replace(/(\$\{PRODUCT_RESIDUAL\}|PRODUCT_RESIDUAL)/g, formatCurrency(product.residualValue || '0', currency))
        .replace(
          /(\$\{PRODUCT_MONTHLY_FEE\}|PRODUCT_MONTHLY_FEE)/g,
          formatCurrency((product.totalCost || 0) / months, currency),
        )
        .replace(/(\$\{PRODUCT_TOTAL_PRICE\}|PRODUCT_TOTAL_PRICE)/g, formatCurrency(product.totalCost || 0, currency))
        .replace(/(\$\{TOTAL_LENGTH\}|TOTAL_LENGTH)/g, String(months))
    })
    .join('\n')
}

/**
 * A function generating a HTML string from Contract data
 * @param t Translation function
 * @param formData Contract data
 * @param template Contract template string
 * @param imageUrl Company image src
 * @returns Formatted HTML string of the Contract data
 */
export function generateTemplateHtml(formData: ContractHTMLData, template: string, imageUrl: string, currency: string) {
  const totalCost = getTotalContractCost(formData.products, formData.extraCosts)
  const products = formData.products.flatMap(item => [item, ...item.accessories])
  const productRowMatch = template.match(/<product-list[\s\S]*?<\/product-list>/)?.[0] || ''

  const productRowsHtml = generateProductRows(productRowMatch, products, formData.length, currency)

  const replacedTemplateKeysHtml = replaceTemplateKeys(template, formData, templateLiterals)
  const replacedTemplateWithProductsHtml = replacedTemplateKeysHtml.replace(
    /<product-list[\s\S]*?<\/product-list>/,
    productRowsHtml,
  )

  const replacedTemplateWithProductsAndTotalHtml = replacedTemplateWithProductsHtml
    .replace(/(\$\{TOTAL_MONTHLY_FEE\}|TOTAL_MONTHLY_FEE)/g, formatCurrency(totalCost / formData.length, currency))
    .replace(/(\$\{TOTAL_TOTAL\}|TOTAL_TOTAL)/g, formatCurrency(totalCost, currency))
    .replace(/(\$\{COMPANY_LOGO\}|COMPANY_LOGO)/g, imageUrl)

  return replacedTemplateWithProductsAndTotalHtml
}
